import { inspectorsQuery, salesRepsQuery, useUsers } from "@common/api/users"
import { signersQuery } from "@common/api/work-packages"
import type { SignerKind, SignerPublic, UserBasicInfo } from "@common/client"
import { UserInitials } from "@common/components/shared/user-initials"
import { Button } from "@common/components/ui/button"
import { FieldTrigger } from "@common/components/ui/field-trigger"
import { Input } from "@common/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@common/components/ui/popover"
import { cn } from "@common/lib/utils"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { useState } from "react"

export type Named = Pick<UserBasicInfo, "id" | "first_name" | "last_name">

const BASELINE_ANCHOR = "\u200B"

function UnassignedAvatar(): React.ReactElement {
  return (
    <div className="flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground text-tiny-medium">
      {BASELINE_ANCHOR}
    </div>
  )
}

export function fullName(user: Named): string {
  return `${user.first_name} ${user.last_name}`.trim()
}

export function Person({ user }: { user: Named }): React.ReactElement {
  return (
    <>
      <UserInitials user={user} title={null} />
      <span className="truncate">{fullName(user)}</span>
    </>
  )
}

type PickerProps = {
  label: string
  user: Named | null
  disabled: boolean
  className?: string
  trigger?: React.ReactElement
  allowSame?: boolean
  clearable?: boolean
  emptyLabel?: string
  onPick: (userId: string | null, user: Named | null) => void
}

export function UserPicker(props: PickerProps): React.ReactElement {
  const [search, setSearch] = useState("")
  const { users } = useUsers({ search: search.trim() || null, is_active: true })

  return (
    <Picker
      {...props}
      candidates={users}
      search={search}
      onSearch={setSearch}
    />
  )
}

export function MemberPicker({
  members,
  ...props
}: PickerProps & { members: Named[] }): React.ReactElement {
  const [search, setSearch] = useState("")
  const needle = search.trim().toLowerCase()

  return (
    <Picker
      {...props}
      candidates={members.filter((member) =>
        fullName(member).toLowerCase().includes(needle),
      )}
      search={search}
      onSearch={setSearch}
    />
  )
}

export function SalesRepPicker(props: PickerProps): React.ReactElement {
  const [search, setSearch] = useState("")
  const { data } = useQuery({
    ...salesRepsQuery({ search: search.trim() || null }),
    placeholderData: keepPreviousData,
  })

  return (
    <Picker
      {...props}
      candidates={data?.data ?? []}
      search={search}
      onSearch={setSearch}
    />
  )
}

function signerUnavailable(signer: SignerPublic): string | null {
  if (!signer.has_signing_pin) return "No PIN"
  return signer.signing_pin_locked ? "PIN locked" : null
}

export function SignerPicker({
  kind,
  value,
  ...props
}: Omit<PickerProps, "user"> & {
  kind: SignerKind
  value: string | null
}): React.ReactElement {
  const [search, setSearch] = useState("")
  const { data, refetch } = useQuery(signersQuery({ kind }))
  const needle = search.trim().toLowerCase()
  const signers = data?.data ?? []
  const reasons = new Map(
    signers.map((each) => [each.id, signerUnavailable(each)]),
  )

  return (
    <Picker
      {...props}
      user={signers.find(({ id }) => id === value) ?? null}
      candidates={signers.filter((signer) =>
        fullName(signer).toLowerCase().includes(needle),
      )}
      onOpen={() => refetch()}
      unavailable={(candidate) => reasons.get(candidate.id) ?? null}
      search={search}
      onSearch={setSearch}
    />
  )
}

export function InspectorPicker(props: PickerProps): React.ReactElement {
  const [search, setSearch] = useState("")
  const { data } = useQuery({
    ...inspectorsQuery({ search: search.trim() || null }),
    placeholderData: keepPreviousData,
  })

  return (
    <Picker
      {...props}
      candidates={data?.data ?? []}
      search={search}
      onSearch={setSearch}
    />
  )
}

function Picker({
  label,
  user,
  disabled,
  className,
  trigger,
  allowSame = false,
  clearable = true,
  emptyLabel = "Unassigned",
  onPick,
  candidates,
  unavailable,
  onOpen,
  search,
  onSearch,
}: PickerProps & {
  candidates: Named[]
  unavailable?: (candidate: Named) => string | null
  onOpen?: () => void
  search: string
  onSearch: (search: string) => void
}): React.ReactElement {
  const [open, setOpen] = useState(false)

  const pick = (picked: Named | null) => {
    const userId = picked?.id ?? null
    if (allowSame || userId !== (user?.id ?? null)) onPick(userId, picked)
    setOpen(false)
    onSearch("")
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) onOpen?.()
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        {trigger ?? (
          <FieldTrigger
            aria-label={label}
            disabled={disabled}
            className={cn("px-2 sm:w-auto sm:max-w-64", className)}
          >
            {user ? (
              <Person user={user} />
            ) : (
              <>
                <UnassignedAvatar />
                <span className="text-muted-foreground">{emptyLabel}</span>
              </>
            )}
          </FieldTrigger>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-1 p-1">
        <Input
          autoFocus
          aria-label={`Search ${label.toLowerCase()}`}
          placeholder="Search people…"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
        />
        <div className="max-h-64 overflow-y-auto">
          {clearable && (
            <Button
              variant="ghost"
              className="w-full justify-start gap-2 px-2 text-body-regular text-muted-foreground"
              onClick={() => pick(null)}
            >
              <UnassignedAvatar />
              Unassigned
            </Button>
          )}
          {candidates.map((candidate) => {
            const reason = unavailable?.(candidate) ?? null
            return (
              <Button
                key={candidate.id}
                variant="ghost"
                className="w-full justify-start gap-2 px-2 text-body-regular"
                disabled={reason !== null}
                onClick={() => pick(candidate)}
              >
                <UserInitials user={candidate} title={null} />
                <span className="truncate">{fullName(candidate)}</span>
                {reason && (
                  <span className="ml-auto text-small-regular text-muted-foreground">
                    {reason}
                  </span>
                )}
              </Button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
